import { IntervalsRange } from "@/engine/engine";
import {
  HGIntersection,
  HGIntersectionType,
  HGInterval,
  HGTimeRecordPlus,
} from "./types";

export function calculateIntersection(
  parentRange: [bigint, bigint],
  childRange: [bigint, bigint | null],
): HGIntersection {
  const parentStart = parentRange[0];
  const parentEnd = parentRange[1];

  const childStart = childRange[0];
  const childEnd = childRange[1];

  // `childEnd == null` is the unfinished record, and is the only thing that
  // may stand in for "no end": a plain truthiness test on a bigint would read
  // second 0 as unfinished.
  const unfinished = childEnd == null;

  if (
    parentStart > parentEnd ||
    childStart > parentEnd ||
    (!unfinished && (childEnd < childStart || childEnd < parentStart))
  ) {
    return {
      offsets: null,
      intersectionType: HGIntersectionType.None,
    };
  }

  let intersectionType = HGIntersectionType.None;
  let begin = 0;
  let end = 100;
  let finished = true;
  // Both ends included, and parentStart <= parentEnd past the guard above.
  const parentDuration = Number(parentEnd - parentStart) + 1;

  // `begin` and `end` are percentages of the way through the parent, ready to
  // draw the child as a bar across it. The leading edge of a child that starts
  // where the parent does is at 0%; the trailing edge of one that ends where
  // the parent does is at 100%, which is why `end` counts the child's own last
  // second in and `begin` does not. Both are measured from `parentStart`: it is
  // an offset within the parent, never an absolute second.
  const beginOffset = (second: bigint) =>
    (Number(second - parentStart) * 100) / parentDuration;
  const endOffset = (second: bigint) =>
    (Number(second - parentStart + 1n) * 100) / parentDuration;

  if (unfinished) {
    finished = false;
    if (childStart < parentStart) {
      intersectionType = HGIntersectionType.Transit;
    } else if (childStart == parentStart) {
      intersectionType = HGIntersectionType.Starts;
    } else {
      intersectionType = HGIntersectionType.PartRight;
      begin = beginOffset(childStart);
    }
  } else if (childStart == parentStart && childEnd == parentEnd) {
    intersectionType = HGIntersectionType.Exact;
  } else if (childStart == parentStart && childEnd > parentEnd) {
    intersectionType = HGIntersectionType.Starts;
  } else if (childStart < parentStart && childEnd == parentEnd) {
    intersectionType = HGIntersectionType.Ends;
  } else if (childStart < parentStart && childEnd > parentEnd) {
    intersectionType = HGIntersectionType.Transit;
  } else if (childStart >= parentStart && childEnd <= parentEnd) {
    intersectionType = HGIntersectionType.Contained;
    begin = beginOffset(childStart);
    end = endOffset(childEnd);
  } else if (childStart >= parentStart) {
    intersectionType = HGIntersectionType.PartRight;
    begin = beginOffset(childStart);
  } else if (childEnd <= parentEnd) {
    intersectionType = HGIntersectionType.PartLeft;
    end = endOffset(childEnd);
  }

  const intersection = {
    offsets: {
      begin,
      end,
      finished,
    },
    intersectionType,
  };

  return intersection;
}

export function calculateIntersectionOfRecordWithIntervals(
  record: HGTimeRecordPlus,
  interval: HGInterval,
  endInterval: HGInterval | null,
) {
  const intervalRange = IntervalsRange(interval, endInterval, "s");
  const recordRange: [bigint, bigint | null] = [
    record.start_second,
    record.end_interval ? record.end_second : null,
  ];
  return calculateIntersection(intervalRange, recordRange);
}
